import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Bell, CheckCircle, Thermometer, Wind, Droplets, Sun, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { API_BASE_URL } from '@/config/api';

interface UserAlert {
  id: string;
  threshold_id: string;
  alert_type: string;
  severity: string;
  title: string;
  message: string;
  current_value: number;
  threshold_value: number;
  is_read: boolean;
  is_dismissed: boolean;
  created_at: string;
}

export function AlertListCard() {  const { t } = useTranslation();  const [alerts, setAlerts] = useState<UserAlert[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { token } = useAuthStore();

  useEffect(() => {
    loadAlerts();
    loadUnreadCount();
  }, []);

  const loadAlerts = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/alerts?limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/alerts/unread-count`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const markAsRead = async (alertId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/alerts/${alertId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await loadAlerts();
        await loadUnreadCount();
      }
    } catch (error) {
      console.error('Failed to mark alert as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/alerts/mark-all-read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await loadAlerts();
        await loadUnreadCount();
      }
    } catch (error) {
      console.error('Failed to mark all alerts as read:', error);
    }
  };

  const dismissAlert = async (alertId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/alerts/${alertId}/dismiss`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await loadAlerts();
      }
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'temperature':
        return <Thermometer className="w-5 h-5" />;
      case 'wind':
        return <Wind className="w-5 h-5" />;
      case 'rain':
        return <Droplets className="w-5 h-5" />;
      case 'uv':
        return <Sun className="w-5 h-5" />;
      case 'humidity':
        return <Droplets className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'from-red-500 to-red-600';
      case 'warning':
        return 'from-yellow-500 to-yellow-600';
      case 'info':
      default:
        return 'from-blue-500 to-blue-600';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div className="bg-surface rounded-xl p-4 md:p-6 border border-border shadow-sm">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div>
            <h3 className="text-base md:text-lg font-bold text-text-primary">
              {t('alerts.recent')}
            </h3>
            {unreadCount > 0 && (
              <p className="text-xs md:text-sm text-text-secondary">{unreadCount} neue Warnung{unreadCount !== 1 ? 'en' : ''}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          {alerts.length > 0 && unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex-1 sm:flex-none px-3 py-1 text-xs md:text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors whitespace-nowrap"
            >
              Alle gelesen
            </button>
          )}
          <button
            onClick={loadAlerts}
            className="p-2 hover:bg-surface-elevated rounded-lg transition-colors"
            aria-label="Aktualisieren"
          >
            <RefreshCw className="w-4 h-4 text-text-primary" />
          </button>
        </div>
      </div>

      {/* Alerts List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="text-center py-12">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-text-primary mb-2">
            {t('alerts.noAlerts')}
          </h4>
          <p className="text-sm text-text-secondary">
            {t('ui.allLooksGood')}
          </p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[400px] md:max-h-[500px] overflow-y-auto">
          {alerts.filter(alert => !alert.is_dismissed).map((alert) => (
            <motion.div
              key={alert.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className={`p-3 md:p-4 rounded-lg border ${
                alert.is_read 
                  ? 'border-border bg-surface-elevated opacity-70' 
                  : 'border-blue-200 dark:border-blue-800 alert-card-unread'
              }`}
            >
              <div className="flex items-start gap-2 md:gap-3">
                <div className={`p-1.5 md:p-2 rounded-lg bg-linear-to-br ${getSeverityColor(alert.severity)} text-white flex-shrink-0`}>
                  {getAlertIcon(alert.alert_type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 mb-1">
                    <h4 className="font-semibold text-sm md:text-base text-text-primary truncate pr-2">{alert.title}</h4>
                    <span className="text-xs text-text-secondary whitespace-nowrap flex-shrink-0">{formatDate(alert.created_at)}</span>
                  </div>
                  
                  <p className="text-xs md:text-sm text-text-secondary mb-2 line-clamp-2">{alert.message}</p>
                  
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-secondary">
                    <span className="whitespace-nowrap">Aktuell: {alert.current_value.toFixed(1)}</span>
                    <span className="hidden sm:inline">•</span>
                    <span className="whitespace-nowrap">Schwellenwert: {alert.threshold_value.toFixed(1)}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2 mt-3">
                {!alert.is_read && (
                  <button
                    onClick={() => markAsRead(alert.id)}
                    className="w-full sm:flex-1 px-3 py-1.5 text-xs md:text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                  >
                    Als gelesen markieren
                  </button>
                )}
                <button
                  onClick={() => dismissAlert(alert.id)}
                  className="w-full sm:w-auto px-3 py-1.5 text-xs md:text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors whitespace-nowrap"
                >
                  Ausblenden
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
